/**
 * Carga asíncrona que además se refresca sola (actualización automática).
 *
 * Es `useAsyncResource` con un ciclo encima, y las garantías que necesita una
 * pantalla que se actualiza mientras alguien la está leyendo:
 *
 * - El refresco de fondo es silencioso: no vuelve a "cargando" ni vacía el dato,
 *   así la tabla, el scroll y lo que el operador tenga a medio escribir quedan
 *   donde estaban.
 * - Un error de fondo conserva el último dato bueno y se informa en `error`; el
 *   ciclo siguiente lo reintenta solo. Salvo que sea de los que no se arreglan
 *   solos (un 404, un 403: ver `stopOnError`): ahí el ciclo se corta, y lo
 *   retoman "Actualizar" (`reload()`) o volver a encender el switch.
 * - Secuencial: nunca hay dos consultas del mismo recurso en vuelo, y la
 *   siguiente se agenda cuando termina la anterior (una API lenta no se apila).
 * - La respuesta de dependencias viejas (cambiaron los filtros o la página
 *   mientras viajaba) se descarta.
 * - Con la pestaña oculta no se consulta ni queda un timer vivo; al volver a
 *   verse se refresca de inmediato.
 * - Encender (`enabled`) refresca de inmediato; apagar no deja ningún timer.
 *
 * Con `enabled` en `false` la pantalla no se rompe: la carga inicial, el cambio
 * de dependencias y `reload()` (el botón "Actualizar") siguen funcionando.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { isPermanentApiError } from '../api/errors';

export interface PolledResourceOptions<T> {
  /** Pausa entre el fin de una consulta y el comienzo de la siguiente, en ms. */
  readonly intervalMs: number;
  /** Actualización automática encendida. */
  readonly enabled: boolean;
  /**
   * Si el dato recién llegado todavía puede cambiar solo. Cuando responde
   * `false` se deja de agendar refrescos (y encender no fuerza uno); `reload()`
   * sigue consultando y, si el dato nuevo vuelve a poder cambiar, el ciclo se
   * retoma. Por defecto, siempre.
   */
  readonly shouldPoll?: (data: T) => boolean;
  /**
   * Si un error corta el ciclo. Por defecto, los que repetir no arregla
   * (`isPermanentApiError`: 404, 403, 400…): abrir el detalle de un mensaje que
   * no existe no puede quedar pidiéndolo cada 5 s mientras la pestaña siga
   * abierta. La red caída o un 5xx siguen reintentándose solos.
   */
  readonly stopOnError?: (error: unknown) => boolean;
  /**
   * Al cambiar las dependencias, seguir mostrando el dato anterior mientras llega
   * el nuevo (por defecto, como `useAsyncResource`). Con `false` se vacía de
   * inmediato: para cuando el dato anterior es otro documento y confundiría.
   */
  readonly keepPreviousData?: boolean;
}

export interface PolledResource<T> {
  readonly data: T | null;
  readonly error: unknown;
  /**
   * Carga en primer plano en curso: al montar, al cambiar las dependencias o con
   * `reload()`. Los refrescos de fondo no la prenden.
   */
  readonly loading: boolean;
  /** Cuándo llegó el dato que se está mostrando (epoch ms); `null` si no hay dato. */
  readonly updatedAt: number | null;
  /**
   * Consulta ya, sin esperar el ciclo. Si hay una consulta en vuelo, se encadena
   * una sola más apenas termine: el dato que queda es posterior al pedido.
   */
  reload: () => void;
}

interface ResourceState<T> {
  /** Dependencias a las que corresponde este estado. */
  readonly deps: readonly unknown[];
  readonly data: T | null;
  readonly error: unknown;
  readonly loading: boolean;
  readonly updatedAt: number | null;
}

/** Lo que el ciclo vigente expone hacia afuera del efecto. */
interface PollerControl {
  refresh(): void;
  setEnabled(enabled: boolean): void;
}

const IDLE_CONTROL: PollerControl = {
  refresh: () => undefined,
  setEnabled: () => undefined,
};

/** `initial`: dependencias nuevas; `reload`: pedido explícito; `background`: el ciclo. */
type RefreshMode = 'initial' | 'reload' | 'background';

function sameDeps(left: readonly unknown[], right: readonly unknown[]): boolean {
  return (
    left.length === right.length && left.every((value, index) => Object.is(value, right[index]))
  );
}

/**
 * Ejecuta `load` al montar y cada vez que cambia `deps`, y la repite cada
 * `intervalMs` mientras `enabled` lo permita. `load` en `null` significa "todavía
 * no hay nada que cargar" (por ejemplo, falta el id): no consulta ni queda cargando.
 */
export function usePolledResource<T>(
  load: (() => Promise<T>) | null,
  deps: readonly unknown[],
  options: PolledResourceOptions<T>,
): PolledResource<T> {
  const keepPreviousData = options.keepPreviousData ?? true;
  // Pasar de "nada que cargar" a una carga también es un cambio de recurso.
  const resourceDeps = [...deps, load === null];

  const [state, setState] = useState<ResourceState<T>>(() => ({
    deps: resourceDeps,
    data: null,
    error: null,
    loading: load !== null,
    updatedAt: null,
  }));

  // `load` y las opciones son valores nuevos en cada render; las referencias
  // evitan relanzar el ciclo por eso y dejan que manden las dependencias.
  const loadRef = useRef(load);
  loadRef.current = load;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const control = useRef<PollerControl>(IDLE_CONTROL);

  useEffect(() => {
    const keyDeps = resourceDeps;

    if (loadRef.current === null) {
      control.current = IDLE_CONTROL;
      setState({ deps: keyDeps, data: null, error: null, loading: false, updatedAt: null });
      return undefined;
    }

    let disposed = false;
    let inFlight = false;
    let queuedReload = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let enabled = optionsRef.current.enabled;
    // La última consulta falló con un error que repetir no arregla (ver `stopOnError`).
    let stoppedByError = false;
    // Último dato bueno DE ESTAS dependencias (y cuándo llegó).
    let latest: { readonly data: T; readonly at: number } | null = null;

    const clearTimer = (): void => {
      if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
      }
    };

    const wantsPolling = (): boolean => {
      if (!enabled || stoppedByError) {
        return false;
      }
      const { shouldPoll } = optionsRef.current;
      return latest === null || shouldPoll === undefined || shouldPoll(latest.data);
    };

    const schedule = (): void => {
      clearTimer();
      // Con la pestaña oculta no queda ni el timer: al volver a verse,
      // `visibilitychange` refresca de inmediato.
      if (disposed || !wantsPolling() || document.hidden) {
        return;
      }
      timer = setTimeout(() => {
        timer = undefined;
        refresh('background');
      }, optionsRef.current.intervalMs);
    };

    const refresh = (mode: RefreshMode): void => {
      const run = loadRef.current;
      if (disposed || run === null) {
        return;
      }
      if (inFlight) {
        // Nunca dos consultas del mismo recurso en vuelo. Un pedido explícito se
        // atiende apenas termine la actual; uno automático sobra, porque la que
        // está en vuelo ya trae un dato fresco y al terminar agenda el ciclo.
        if (mode !== 'background') {
          queuedReload = true;
        }
        return;
      }
      if (mode === 'background' && document.hidden) {
        // Nadie está mirando: se pausa y `visibilitychange` lo retoma.
        clearTimer();
        return;
      }

      clearTimer();
      inFlight = true;
      if (mode === 'initial') {
        setState((previous) => ({
          deps: keyDeps,
          data: keepPreviousData ? previous.data : null,
          error: null,
          loading: true,
          updatedAt: keepPreviousData ? previous.updatedAt : null,
        }));
      } else if (mode === 'reload') {
        setState((previous) => ({ ...previous, deps: keyDeps, error: null, loading: true }));
      }

      let request: Promise<T>;
      try {
        request = run();
      } catch (error) {
        // Una carga que revienta antes de devolver la promesa es un error más,
        // no una excepción que se escapa del efecto y deja la pantalla colgada.
        request = Promise.reject(error);
      }

      request
        .then(
          (data) => {
            if (disposed) {
              return;
            }
            stoppedByError = false;
            latest = { data, at: Date.now() };
            setState({ deps: keyDeps, data, error: null, loading: false, updatedAt: latest.at });
          },
          (error: unknown) => {
            if (disposed) {
              return;
            }
            stoppedByError = (optionsRef.current.stopOnError ?? isPermanentApiError)(error);
            // Con dato propio se conserva: la tabla no desaparece por un error
            // pasajero. El dato de otras dependencias no se hace pasar por la
            // respuesta a estas.
            setState({
              deps: keyDeps,
              data: latest === null ? null : latest.data,
              error,
              loading: false,
              updatedAt: latest === null ? null : latest.at,
            });
          },
        )
        .finally(() => {
          inFlight = false;
          if (disposed) {
            return;
          }
          if (queuedReload) {
            queuedReload = false;
            refresh('reload');
            return;
          }
          schedule();
        });
    };

    const onVisibilityChange = (): void => {
      if (document.hidden) {
        clearTimer();
        return;
      }
      if (wantsPolling()) {
        refresh('background');
      }
    };

    control.current = {
      refresh: () => refresh('reload'),
      setEnabled: (next) => {
        if (next === enabled) {
          return;
        }
        enabled = next;
        if (!next) {
          clearTimer();
          return;
        }
        // Encender es un pedido explícito: vuelve a intentar aunque el último
        // error haya cortado el ciclo. Y se nota enseguida, salvo que el dato
        // diga que ya no cambia.
        stoppedByError = false;
        if (wantsPolling()) {
          refresh('background');
        }
      },
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    refresh('initial');

    return () => {
      disposed = true;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      control.current = IDLE_CONTROL;
    };
    // Las dependencias las declara quien llama, como en `useEffect`.
  }, resourceDeps);

  useEffect(() => {
    control.current.setEnabled(options.enabled);
  }, [options.enabled]);

  const reload = useCallback((): void => {
    control.current.refresh();
  }, []);

  // Entre el render con dependencias nuevas y el efecto que las carga, el estado
  // todavía es el de las anteriores: se presenta como carga en curso.
  const current = sameDeps(state.deps, resourceDeps);
  const showData = current || keepPreviousData;
  return {
    data: showData ? state.data : null,
    error: current ? state.error : null,
    loading: current ? state.loading : load !== null,
    updatedAt: showData ? state.updatedAt : null,
    reload,
  };
}
